import React, { useMemo } from 'react';
import { toDate } from '../services/firebase';
import { dayKey, isSameDay } from '../utils/rides';
import { downloadCsv } from '../utils/exportCsv';
import { useLang } from '../lang/LangContext';

export default function Analytics({ rides = [], drivers = [] }) {
  const { t } = useLang();
  const last7 = useMemo(() => {
    const days = [];
    for (let i = 6; i >= 0; i -= 1) {
      const d = new Date();
      d.setHours(12, 0, 0, 0);
      d.setDate(d.getDate() - i);
      days.push(d);
    }
    return days.map((d) => {
      const key = dayKey(d);
      const completed = rides.filter((r) => r.status === 'completed' && dayKey(toDate(r.completedAt || r.updatedAt)) === key);
      const cancelled = rides.filter((r) => r.status === 'cancelled' && dayKey(toDate(r.cancelledAt || r.updatedAt)) === key);
      const fare = completed.reduce((s, r) => s + (Number(r.fare) || 0), 0);
      return {
        label: d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric' }),
        completed: completed.length,
        cancelled: cancelled.length,
        fare,
      };
    });
  }, [rides]);

  const maxCompleted = Math.max(1, ...last7.map((d) => d.completed));
  const todayCompleted = rides.filter((r) => r.status === 'completed' && isSameDay(toDate(r.completedAt || r.updatedAt)));
  const avgFare = todayCompleted.length
    ? Math.round(todayCompleted.reduce((s, r) => s + (Number(r.fare) || 0), 0) / todayCompleted.length)
    : 0;
  const rated = rides.filter((r) => r.rating);
  const avgRating = rated.length
    ? (rated.reduce((s, r) => s + Number(r.rating), 0) / rated.length).toFixed(1)
    : '—';

  // Module D: Dispatch Metrics
  const dispatchMetrics = useMemo(() => {
    const totalRequests = rides.length;
    if (totalRequests === 0) {
      return {
        acceptanceRate: '—',
        completionRate: '—',
        cancellationRate: '—',
        medianAcceptSec: null,
      };
    }

    const assignedRides = rides.filter((r) => r.driverId || ['driver_assigned', 'arrived', 'started', 'completed'].includes(r.status));
    const completedCount = rides.filter((r) => r.status === 'completed').length;
    const cancelledCount = rides.filter((r) => r.status === 'cancelled').length;

    const acceptanceRate = `${Math.round((assignedRides.length / totalRequests) * 100)}%`;
    const completionRate = `${Math.round((completedCount / totalRequests) * 100)}%`;
    const cancellationRate = `${Math.round((cancelledCount / totalRequests) * 100)}%`;

    // Calculate accept times
    const acceptTimesSec = [];
    rides.forEach((r) => {
      const created = toDate(r.createdAt);
      const assigned = toDate(r.assignedAt || r.acceptedAt);
      if (created && assigned && assigned >= created) {
        const sec = Math.round((assigned.getTime() - created.getTime()) / 1000);
        if (sec > 0 && sec < 600) acceptTimesSec.push(sec);
      }
    });

    let medianAcceptSec = null;
    if (acceptTimesSec.length > 0) {
      acceptTimesSec.sort((a, b) => a - b);
      medianAcceptSec = acceptTimesSec[Math.floor(acceptTimesSec.length / 2)];
    }

    return {
      acceptanceRate,
      completionRate,
      cancellationRate,
      medianAcceptSec,
    };
  }, [rides]);

  // Hourly Demand Distribution (6 AM - 11 PM)
  const hourlyDemand = useMemo(() => {
    const hours = Array.from({ length: 18 }, (_, i) => i + 6); // 6 AM to 23 PM
    return hours.map((h) => {
      const count = rides.filter((r) => {
        const d = toDate(r.createdAt);
        return d && d.getHours() === h;
      }).length;
      return {
        hour: `${h > 12 ? h - 12 : h} ${h >= 12 ? 'PM' : 'AM'}`,
        count,
      };
    });
  }, [rides]);

  const maxHourly = Math.max(1, ...hourlyDemand.map((h) => h.count));

  // Top Corridors
  const topCorridors = useMemo(() => {
    const map = {};
    rides.filter((r) => r.pickup?.address && r.destination?.name).forEach((r) => {
      const key = `${r.pickup.address} → ${r.destination.name}`;
      if (!map[key]) {
        map[key] = { route: key, rides: 0, fare: 0 };
      }
      map[key].rides += 1;
      map[key].fare += Number(r.fare) || 0;
    });
    return Object.values(map).sort((a, b) => b.rides - a.rides).slice(0, 5);
  }, [rides]);

  const topCaptains = useMemo(() => {
    const map = {};
    rides.filter((r) => r.status === 'completed' && r.driverId).forEach((r) => {
      if (!map[r.driverId]) {
        map[r.driverId] = { id: r.driverId, name: r.driverName || 'Captain', rides: 0, fare: 0 };
      }
      map[r.driverId].rides += 1;
      map[r.driverId].fare += Number(r.fare) || 0;
    });
    return Object.values(map).sort((a, b) => b.rides - a.rides).slice(0, 8);
  }, [rides]);

  return (
    <>
      {/* KPI Cards */}
      <div className="stat-grid">
        <div className="stat-card blue">
          <div className="stat-label">{t('TODAY COMPLETED')}</div>
          <div className="stat-value blue">{todayCompleted.length}</div>
        </div>
        <div className="stat-card green">
          <div className="stat-label">{t('AVG FARE')}</div>
          <div className="stat-value green">₹{avgFare}</div>
        </div>
        <div className="stat-card orange">
          <div className="stat-label">{t('AVG RATING')}</div>
          <div className="stat-value orange">{avgRating}★</div>
        </div>
        <div className="stat-card red">
          <div className="stat-label">{t('ACTIVE ANNAS')}</div>
          <div className="stat-value">{drivers.filter((d) => d.isApproved).length}</div>
        </div>
      </div>

      {/* Module D: Dispatch Analytics Banner */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title">{t('🧪 Dispatch & Acceptance Performance')}</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, marginTop: 12 }}>
          <div style={{ background: '#0f172a', padding: '14px 18px', borderRadius: 10, border: '1px solid #334155' }}>
            <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 700 }}>{t('MEDIAN TIME TO ACCEPT')}</span>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#38bdf8', marginTop: 4 }}>
              {dispatchMetrics.medianAcceptSec != null ? `${dispatchMetrics.medianAcceptSec}s` : '—'}
            </div>
            <small className="muted">{dispatchMetrics.medianAcceptSec != null ? 'Request ➔ Anna Acceptance' : 'No accept records yet'}</small>
          </div>

          <div style={{ background: '#0f172a', padding: '14px 18px', borderRadius: 10, border: '1px solid #334155' }}>
            <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 700 }}>{t('FLEET ACCEPTANCE RATE')}</span>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#10b981', marginTop: 4 }}>
              {dispatchMetrics.acceptanceRate}
            </div>
            <small className="muted">{t('Successful matches')}</small>
          </div>

          <div style={{ background: '#0f172a', padding: '14px 18px', borderRadius: 10, border: '1px solid #334155' }}>
            <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 700 }}>{t('TRIP COMPLETION RATE')}</span>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#00ff9d', marginTop: 4 }}>
              {dispatchMetrics.completionRate}
            </div>
            <small className="muted">{t('Fulfilled passenger rides')}</small>
          </div>

          <div style={{ background: '#0f172a', padding: '14px 18px', borderRadius: 10, border: '1px solid #334155' }}>
            <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 700 }}>{t('CANCELLATION RATE')}</span>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#ef4444', marginTop: 4 }}>
              {dispatchMetrics.cancellationRate}
            </div>
            <small className="muted">{t('Cancelled bookings')}</small>
          </div>
        </div>
      </div>

      {/* Hourly Demand Curve */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title">⏰ Hourly Demand Distribution (Piler Rush Hours)</div>
        <p className="muted" style={{ fontSize: 12, margin: '2px 0 12px' }}>
          Identifies morning and evening peak hours to position Annas at key stands.
        </p>
        <div className="bar-chart" style={{ height: 160, display: 'flex', alignItems: 'flex-end', gap: 6 }}>
          {hourlyDemand.map((h) => (
            <div key={h.hour} className="bar-col" style={{ flex: 1 }}>
              <div className="bar-stack" style={{ height: 110 }}>
                <div
                  className="bar-fill"
                  style={{
                    height: `${(h.count / maxHourly) * 100}%`,
                    background: h.count > 0 ? '#38bdf8' : '#334155',
                  }}
                  title={`${h.count} ride requests at ${h.hour}`}
                />
              </div>
              <div className="bar-label" style={{ fontSize: 10, transform: 'rotate(-45deg)', transformOrigin: 'left bottom', marginTop: 10 }}>
                {h.hour}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 7 Days Trend */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title" style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>Last 7 days Volume</span>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => downloadCsv('analytics-7d.csv', [
              ['day', 'completed', 'cancelled', 'fare'],
              ...last7.map((d) => [d.label, d.completed, d.cancelled, d.fare]),
            ])}
          >
            Export CSV
          </button>
        </div>
        <div className="bar-chart">
          {last7.map((d) => (
            <div key={d.label} className="bar-col">
              <div className="bar-stack">
                <div
                  className="bar-fill"
                  style={{ height: `${(d.completed / maxCompleted) * 100}%` }}
                  title={`${d.completed} completed`}
                />
              </div>
              <div className="bar-label">{d.label}</div>
              <div className="bar-meta">{d.completed}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Top Corridors & Top Annas */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>
        <div className="card">
          <div className="card-title">{t('📍 Top Piler Corridors (High Demand Routes)')}</div>
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>ROUTE</th>
                  <th>RIDES</th>
                  <th>GMV</th>
                </tr>
              </thead>
              <tbody>
                {topCorridors.length === 0 ? (
                  <tr><td colSpan={3} className="empty">{t('No corridor data yet')}</td></tr>
                ) : topCorridors.map((c) => (
                  <tr key={c.route}>
                    <td style={{ fontWeight: 600 }}>{c.route}</td>
                    <td>{c.rides}</td>
                    <td><strong>₹{c.fare}</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card">
          <div className="card-title">{t('🏆 Top Annas (Completed Rides)')}</div>
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>CAPTAIN</th>
                  <th>RIDES</th>
                  <th>FARE</th>
                </tr>
              </thead>
              <tbody>
                {topCaptains.length === 0 ? (
                  <tr><td colSpan={3} className="empty">{t('No completed rides yet')}</td></tr>
                ) : topCaptains.map((c) => (
                  <tr key={c.id}>
                    <td>{c.name}</td>
                    <td>{c.rides}</td>
                    <td><strong>₹{c.fare}</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}
